import axios from 'axios';
import { User } from '../contexts/UserContext';
import { API_CONFIG, getApiUrl } from '../config/apiConfig';

interface LoginCredentials {
  username: string;
  password: string;
  FcmToken?: string;
}

interface ChangePasswordRequest {
  username: string;
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

interface ChangePasswordResponse {
  success: boolean;
  message: string;
  passwordChangedAt?: string;
}

interface AuthResponse {
  token: string;
  user: User;
}

class AuthService {
  private static instance: AuthService;
  private token: string | null = null;

  private constructor() {
    this.token = localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private getHeaders() {
    this.token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
    };
  }

  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // First call to validate user and get token
      const fcmToken =
        credentials.FcmToken ??
        localStorage.getItem('FCMKey') ??
        localStorage.getItem('FcmToken') ??
        localStorage.getItem('fcmToken') ??
        sessionStorage.getItem('FCMKey') ??
        sessionStorage.getItem('FcmToken') ??
        sessionStorage.getItem('fcmToken') ??
        '';

      const validateResponse = await axios.post(`${API_CONFIG.BASE_URL}/Auth/ValidateUser`, {
        ...credentials,
        FcmToken: fcmToken,
      });
      const token = validateResponse.data.token;

      // Second call to get user details
      const userResponse = await axios.post(`${API_CONFIG.BASE_URL}/User/GetUserDetails`, {
        ...credentials,
        FcmToken: fcmToken,
      });

      this.token = token;
      return {
        token,
        user: userResponse.data
      };
    } catch (error) {
      throw new Error('Invalid credentials');
    }
  }

  public async validateToken(): Promise<AuthResponse> {
    try {
      this.token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post<AuthResponse>(
        getApiUrl(API_CONFIG.ENDPOINTS.AUTH.VALIDATE),
        { token: this.token },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async logout(): Promise<void> {
    this.token = null;
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
  }

  public async changePassword(request: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/User/ChangePassword`,
        request,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      return new Error(error.response?.data?.message || 'An error occurred');
    }
    return error;
  }
}

export const authService = AuthService.getInstance(); 